import React, { useEffect, useRef, useState, useCallback } from "react";
import { MessageSquareDot, Send, Loader2, Trash2, Plus, Users, Hash, ArrowLeft, Radio, X, Pencil } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChatAvatar from "@/components/ChatAvatar";
import ContextualHint from "@/components/ContextualHint";
import { toast } from "sonner";

const clearedKey = (room) => `chat_cleared_${room}`;
const REACTS = ["🙏", "❤️", "🙌"];

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rooms, setRooms] = useState([]);
  const [liveShow, setLiveShow] = useState({ active: false, slug: "live-show", name: "Live Show" });
  const [mode, setMode] = useState("rooms"); // "rooms" | "direct"
  const [activeRoom, setActiveRoom] = useState("global");
  const [activeLabel, setActiveLabel] = useState("General");

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [clearedAt, setClearedAt] = useState(localStorage.getItem(clearedKey("global")) || "");

  // DM state
  const [contacts, setContacts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [dmView, setDmView] = useState("list"); // "list" | "thread"
  const [dmName, setDmName] = useState("");
  const [showContacts, setShowContacts] = useState(false);
  const [unread, setUnread] = useState({ total: 0, threads: {} });
  const [roomForm, setRoomForm] = useState(null); // { mode:'create'|'edit', slug, name, description }
  const [floats, setFloats] = useState([]); // {key, msgId, emoji}
  const prevReactRef = useRef({});

  const scrollRef = useRef(null);

  const spawnFloat = useCallback((msgId, emoji) => {
    const key = `${msgId}-${emoji}-${Date.now()}-${Math.random()}`;
    setFloats((f) => [...f, { key, msgId, emoji }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.key !== key)), 900);
  }, []);

  const loadUnread = useCallback(() => {
    api.get("/chat/unread").then((r) => setUnread(r.data)).catch(() => {});
  }, []);
  const markRead = useCallback((room) => {
    api.post("/chat/read", { room }).then(() => loadUnread()).catch(() => {});
  }, [loadUnread]);

  const loadRooms = useCallback(() => {
    api.get("/chat/rooms").then((r) => { setRooms(r.data.rooms); setLiveShow(r.data.live_show); }).catch(() => {});
  }, []);

  const loadMessages = useCallback((room) => {
    api.get(`/chat?room=${encodeURIComponent(room)}`).then((res) => { setMessages(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const loadThreads = useCallback(() => {
    api.get("/chat/dm/list").then((r) => setThreads(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadRooms(); const t = setInterval(loadRooms, 20000); return () => clearInterval(t); }, [loadRooms]);
  useEffect(() => { loadUnread(); const t = setInterval(loadUnread, 8000); return () => clearInterval(t); }, [loadUnread]);

  // Poll active room messages
  useEffect(() => {
    if (mode === "direct" && dmView === "list") return;
    setLoading(true);
    prevReactRef.current = {};
    setClearedAt(localStorage.getItem(clearedKey(activeRoom)) || "");
    loadMessages(activeRoom);
    if (activeRoom.startsWith("dm:")) markRead(activeRoom);
    const t = setInterval(() => {
      loadMessages(activeRoom);
      if (activeRoom.startsWith("dm:")) markRead(activeRoom);
    }, 4000);
    return () => clearInterval(t);
  }, [activeRoom, mode, dmView, loadMessages, markRead]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  // Animate reactions that arrive (from me or others) by comparing against the previous poll
  useEffect(() => {
    const prev = prevReactRef.current;
    const next = {};
    const seeded = Object.keys(prev).length > 0;
    messages.forEach((m) => {
      next[m.id] = m.reactions || {};
      if (!seeded) return;
      const before = prev[m.id] || {};
      Object.entries(m.reactions || {}).forEach(([emo, count]) => {
        if (count > (before[emo] || 0)) spawnFloat(m.id, emo);
      });
    });
    prevReactRef.current = next;
  }, [messages, spawnFloat]);

  // Refresh DM conversation list (and unread badges) while viewing it
  useEffect(() => {
    if (mode !== "direct" || dmView !== "list") return;
    loadThreads();
    const t = setInterval(loadThreads, 6000);
    return () => clearInterval(t);
  }, [mode, dmView, loadThreads]);

  const openRoom = (slug, label) => {
    setMode("rooms"); setActiveRoom(slug); setActiveLabel(label);
  };

  const openDirect = () => {
    setMode("direct"); setDmView("list"); loadThreads();
    api.get("/chat/contacts").then((r) => setContacts(r.data)).catch(() => {});
  };

  const openThread = (room, name) => {
    setActiveRoom(room); setDmName(name); setDmView("thread"); setShowContacts(false);
    markRead(room);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text;
    setText("");
    try {
      const { data } = await api.post("/chat", { text: t, room: activeRoom });
      setMessages((prev) => [...prev, data]);
      if (activeRoom.startsWith("dm:")) loadThreads();
    } catch (e) { toast.error("Could not send message"); }
  };

  const reactToMsg = async (id, emoji) => {
    if (!user) { toast.error("Sign in to react"); return; }
    try {
      const { data } = await api.post(`/chat/${id}/react`, { emoji });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, reactions: data.reactions, my_reactions: data.my_reactions } : m)));
    } catch (e) { toast.error("Could not add reaction"); }
  };

  const clearChat = async () => {
    const publicRoom = !activeRoom.startsWith("dm:");
    if (isAdmin && publicRoom) {
      if (!window.confirm(`Clear ALL messages in ${activeLabel} for everyone? This cannot be undone.`)) return;
      try { await api.delete(`/chat?room=${encodeURIComponent(activeRoom)}`); setMessages([]); toast.success("Chat cleared for everyone"); }
      catch (e) { toast.error("Could not clear chat"); }
    } else {
      const now = new Date().toISOString();
      localStorage.setItem(clearedKey(activeRoom), now);
      setClearedAt(now);
      toast.success("Cleared from your view");
    }
  };

  const saveRoom = async () => {
    if (!roomForm.name.trim() && roomForm.slug !== "global") { toast.error("Room name is required"); return; }
    try {
      if (roomForm.mode === "edit") {
        const { data } = await api.put(`/chat/rooms/${roomForm.slug}`, { name: roomForm.name.trim(), description: roomForm.description.trim() });
        if (activeRoom === data.slug) setActiveLabel(data.name);
        toast.success("Room updated");
      } else {
        const { data } = await api.post("/chat/rooms", { name: roomForm.name.trim(), description: roomForm.description.trim() });
        openRoom(data.slug, data.name);
        toast.success(`Room "${data.name}" created`);
      }
      setRoomForm(null); loadRooms();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save room"); }
  };

  const deleteRoom = async (slug, name, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the "${name}" room and all its messages?`)) return;
    try {
      await api.delete(`/chat/rooms/${slug}`);
      if (activeRoom === slug) openRoom("global", "General");
      loadRooms(); toast.success("Room deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete room"); }
  };

  const visibleMessages = clearedAt ? messages.filter((m) => (m.created_at || "") > clearedAt) : messages;
  const inThread = mode === "direct" && dmView === "thread";
  const showMessagePane = mode === "rooms" || inThread;
  const activeRoomObj = activeRoom === "live-show" ? liveShow : rooms.find((r) => r.slug === activeRoom);
  const activeDescription = mode === "direct"
    ? (inThread ? `Private conversation with ${dmName}` : "Your private conversations — only you and the recipient can see them.")
    : (activeRoomObj?.description || "Fellowship with listeners around the world.");
  const canEditRoom = isAdmin && mode === "rooms" && activeRoom !== "live-show";

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10 h-[calc(100vh-40px)] flex flex-col max-w-4xl">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-3 min-w-0">
          <MessageSquareDot className="w-5 h-5 text-[#E6B34A] shrink-0" />
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter truncate">Live Chat</h1>
          <span className="flex items-center gap-1 text-[#FF3B30] ml-1 shrink-0"><span className="w-2 h-2 rounded-full bg-[#FF3B30] live-dot" /><span className="overline">Live</span></span>
        </div>
        {showMessagePane && (
          <button onClick={clearChat} data-testid="chat-clear-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-zinc-300 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs font-semibold transition-colors shrink-0">
            <Trash2 className="w-4 h-4" /> {isAdmin && !activeRoom.startsWith("dm:") ? "Clear chat" : "Clear my view"}
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 mb-4">
        <p className="text-zinc-400" data-testid="chat-room-desc">{activeDescription}</p>
        {canEditRoom && (
          <button onClick={() => setRoomForm({ mode: "edit", slug: activeRoom, name: activeRoomObj?.name || "", description: activeRoomObj?.description || "" })} data-testid="chat-edit-room" className="text-zinc-500 hover:text-[#E6B34A] transition-colors shrink-0" title="Edit topic"><Pencil className="w-4 h-4" /></button>
        )}
      </div>

      {/* Room switcher */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 mb-3" data-testid="chat-room-switcher">
        {rooms.map((r) => (
          <button
            key={r.slug}
            onClick={() => openRoom(r.slug, r.name)}
            data-testid={`chat-room-${r.slug}`}
            className={`group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${mode === "rooms" && activeRoom === r.slug ? "bg-[#E6B34A] text-[#0A1030]" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
          >
            <Hash className="w-3.5 h-3.5" /> {r.name}
            {isAdmin && r.slug !== "global" && (
              <span onClick={(e) => deleteRoom(r.slug, r.name, e)} data-testid={`chat-room-del-${r.slug}`} className="ml-1 opacity-50 hover:opacity-100 hover:text-[#FF3B30]"><X className="w-3.5 h-3.5" /></span>
            )}
          </button>
        ))}
        {liveShow.active && (
          <button onClick={() => openRoom("live-show", "Live Show")} data-testid="chat-room-live" className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${mode === "rooms" && activeRoom === "live-show" ? "bg-[#FF3B30] text-white" : "bg-[#FF3B30]/15 text-[#FF7A70] hover:bg-[#FF3B30]/25"}`}>
            <Radio className="w-3.5 h-3.5" /> Live Show <span className="w-1.5 h-1.5 rounded-full bg-current live-dot" />
          </button>
        )}
        {isAdmin && (
          <button onClick={() => setRoomForm({ mode: "create", name: "", description: "" })} data-testid="chat-add-room" className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-white/5 text-[#E6B34A] hover:bg-white/10 transition-colors"><Plus className="w-4 h-4" /> Room</button>
        )}
        <div className="w-px h-6 bg-white/10 mx-1 shrink-0" />
        <button onClick={openDirect} data-testid="chat-tab-direct" className={`relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${mode === "direct" ? "bg-[#5B7CFA] text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
          <Users className="w-3.5 h-3.5" /> Direct
          {unread.total > 0 && (
            <span data-testid="dm-total-badge" className="ml-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[#FF3B30] text-white text-[10px] font-bold">{unread.total > 99 ? "99+" : unread.total}</span>
          )}
        </button>
      </div>

      {/* New / edit room inline form */}
      {roomForm && (
        <div className="rounded-2xl border border-white/10 bg-[#141C42] p-4 mb-3 space-y-2" data-testid="chat-room-form">
          <div className="flex items-center justify-between">
            <h4 className="font-display font-bold">{roomForm.mode === "edit" ? "Edit room" : "New room"}</h4>
            <button onClick={() => setRoomForm(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {roomForm.slug !== "global" && (
            <input value={roomForm.name} onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })} placeholder="Room name (e.g. Youth)" data-testid="chat-room-name-input" className="w-full rounded-xl bg-[#1E2A5A] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
          )}
          <input value={roomForm.description} onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveRoom()} placeholder="Short topic line shown under the room name" data-testid="chat-room-desc-input" className="w-full rounded-xl bg-[#1E2A5A] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
          <div className="flex gap-2">
            <button onClick={saveRoom} data-testid="chat-room-save" className="px-4 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold text-sm hover:bg-[#CF9A34]">{roomForm.mode === "edit" ? "Save" : "Create"}</button>
            <button onClick={() => setRoomForm(null)} className="px-3 py-2.5 rounded-xl bg-white/5 text-zinc-400 hover:bg-white/10 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Direct message conversation list */}
      {mode === "direct" && dmView === "list" && (
        <div className="flex-1 overflow-y-auto rounded-2xl border border-white/5 bg-[#0C1338] p-4 space-y-2" data-testid="dm-list">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold text-lg">Direct messages</h3>
            <button onClick={() => setShowContacts((s) => !s)} data-testid="dm-new-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#5B7CFA]/15 text-[#AFC0FF] text-sm font-semibold hover:bg-[#5B7CFA]/25"><Plus className="w-4 h-4" /> New message</button>
          </div>
          {showContacts && (
            <div className="rounded-xl border border-white/10 bg-[#141C42] p-2 max-h-64 overflow-y-auto mb-2" data-testid="dm-contacts">
              {contacts.length === 0 ? <p className="text-zinc-600 text-sm p-3">No other members yet.</p> : contacts.map((c) => (
                <button key={c.id} onClick={() => openThread(c.dm_room, c.name)} data-testid={`dm-contact-${c.id}`} className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 text-left transition-colors">
                  <ChatAvatar userId={c.id} name={c.name} />
                  <span className="font-semibold text-sm truncate">{c.name}</span>
                  {c.role === "admin" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#5B7CFA]/30 text-[#AFC0FF] font-bold uppercase ml-auto">DJ</span>}
                </button>
              ))}
            </div>
          )}
          {threads.length === 0 ? (
            <p className="text-zinc-600 text-center py-10">No conversations yet. Start a new message above.</p>
          ) : threads.map((t) => (
            <button key={t.room} onClick={() => openThread(t.room, t.other_name)} data-testid={`dm-thread-${t.other_id}`} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 text-left transition-colors">
              <ChatAvatar userId={t.other_id} name={t.other_name} />
              <div className="min-w-0 flex-1">
                <p className={`text-sm truncate ${t.unread > 0 ? "font-bold text-white" : "font-semibold"}`}>{t.other_name}</p>
                <p className={`text-xs truncate ${t.unread > 0 ? "text-zinc-300" : "text-zinc-500"}`}>{t.last_text}</p>
              </div>
              {t.unread > 0 && (
                <span data-testid={`dm-unread-${t.other_id}`} className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-[#FF3B30] text-white text-[11px] font-bold shrink-0">{t.unread > 99 ? "99+" : t.unread}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Message pane (rooms or DM thread) */}
      {showMessagePane && (
        <>
          {user && !inThread && (
            <ContextualHint storageKey="tip_chat_seen" testid="tip-chat">
              Welcome to the live chat! Type a message below to greet everyone, and tap the little emojis under any message to react.
            </ContextualHint>
          )}
          {inThread && (
            <div className="flex items-center gap-2 mb-3" data-testid="dm-thread-header">
              <button onClick={() => setDmView("list")} data-testid="dm-back" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300"><ArrowLeft className="w-4 h-4" /></button>
              <span className="font-display font-bold">{dmName}</span>
            </div>
          )}
          <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-2xl border border-white/5 bg-[#0C1338] p-5 space-y-4" data-testid="chat-messages">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div>
            ) : visibleMessages.length === 0 ? (
              <p className="text-zinc-600 text-center py-10">{inThread ? "Say hello 👋" : "Be the first to say hello 👋"}</p>
            ) : (
              visibleMessages.map((m) => {
                const mine = user && m.user_id === user.id;
                return (
                  <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                    {!mine && <ChatAvatar userId={m.user_id} name={m.user_name} />}
                    <div className={`flex flex-col max-w-[75%] ${mine ? "items-end" : "items-start"}`}>
                      <div className={`rounded-2xl px-4 py-2.5 ${mine ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-white"}`}>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs font-bold ${mine ? "text-[#0A1030]/70" : "text-[#E6B34A]"}`}>{m.user_name}</span>
                          {m.role === "admin" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#5B7CFA]/30 text-[#AFC0FF] font-bold uppercase">DJ</span>}
                        </div>
                        <p className="text-sm leading-relaxed break-words">{m.text}</p>
                      </div>
                      <div className="relative flex gap-1 mt-1">
                        {floats.filter((f) => f.msgId === m.id).map((f) => (
                          <span key={f.key} className="react-float pointer-events-none absolute left-2 -top-1 text-lg" data-testid="react-float">{f.emoji}</span>
                        ))}
                        {REACTS.map((emo) => {
                          const count = m.reactions?.[emo] || 0;
                          const activeR = m.my_reactions?.includes(emo);
                          return (
                            <button key={emo} onClick={() => reactToMsg(m.id, emo)} data-testid={`react-${m.id}-${emo}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${activeR ? "bg-[#E6B34A]/20 text-[#E6B34A] react-pop" : "bg-white/5 text-zinc-500 hover:bg-white/10"}`}>
                              <span>{emo}</span>{count > 0 && <span className="tabular-nums">{count}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {mine && <ChatAvatar userId={m.user_id} name={m.user_name} />}
                  </div>
                );
              })
            )}
          </div>

          <form onSubmit={send} className="flex gap-2 mt-4" data-testid="chat-form">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={inThread ? `Message ${dmName}…` : "Type a message..."} data-testid="chat-input" className="flex-1 rounded-xl bg-[#1E2A5A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
            <button type="submit" data-testid="chat-send" aria-label="Send" className="w-12 h-12 rounded-xl bg-[#E6B34A] text-[#0A1030] flex items-center justify-center hover:bg-[#CF9A34] transition-colors shrink-0"><Send className="w-5 h-5" /></button>
          </form>
        </>
      )}
    </div>
  );
}
